import React, { useEffect, useRef } from 'react'
import { ArrowRight, CalendarDays, Ticket, Users, X } from 'lucide-react'
import flatpickr from 'flatpickr'
import 'flatpickr/dist/flatpickr.min.css'

export default function BookingModal({ open, title = 'Reserve Your Voyage', cruiseTitle, onClose }) {
  const checkinRef = useRef(null)
  const checkoutRef = useRef(null)

  useEffect(() => {
    if (!open) return

    const onKeyDown = (event) => {
      if (event.key === 'Escape') onClose?.()
    }

    window.addEventListener('keydown', onKeyDown)
    document.body.style.overflow = 'hidden'

    return () => {
      window.removeEventListener('keydown', onKeyDown)
      document.body.style.overflow = ''
    }
  }, [open, onClose])

  useEffect(() => {
    if (!open || !checkinRef.current || !checkoutRef.current) return

    const commonOptions = {
      altInput: true,
      altFormat: 'M j, Y',
      dateFormat: 'Y-m-d',
      minDate: 'today',
      disableMobile: true,
      appendTo: document.body,
      onReady: (selectedDates, dateStr, instance) => {
        instance.calendarContainer.classList.add('ljn-booking-datepicker')
      },
    }

    const checkoutPicker = flatpickr(checkoutRef.current, commonOptions)
    const checkinPicker = flatpickr(checkinRef.current, {
      ...commonOptions,
      onChange: ([date]) => {
        if (!date) return

        checkoutPicker.set('minDate', date)
      },
    })

    return () => {
      checkinPicker.destroy()
      checkoutPicker.destroy()
    }
  }, [open])

  if (!open) return null

  const handleSubmit = (event) => {
    event.preventDefault()
  }

  return (
    <div className="ljn-booking-modal" role="dialog" aria-modal="true" aria-labelledby="ljn-booking-title">
      <button className="ljn-booking-modal__overlay" type="button" aria-label="Close booking form" onClick={onClose} />

      <form className="ljn-booking-modal__panel" onSubmit={handleSubmit}>
        <button className="ljn-booking-modal__close" type="button" aria-label="Close booking form" onClick={onClose}>
          <X size={18} strokeWidth={1.7} />
        </button>

        <div className="ljn-booking-modal__header">
          <span className="ljn-booking-modal__kicker">Direct Reservation</span>
          <h2 id="ljn-booking-title" className="ljn-booking-modal__title">{title}</h2>
          {cruiseTitle && <p className="ljn-booking-modal__subtitle">{cruiseTitle}</p>}
        </div>

        <div className="ljn-booking-modal__grid">
          <label className="ljn-booking-field">
            <span>Check-in</span>
            <div className="ljn-booking-field__control">
              <CalendarDays size={16} strokeWidth={1.6} />
              <input ref={checkinRef} type="text" name="checkin" placeholder="Select date" readOnly />
            </div>
          </label>

          <label className="ljn-booking-field">
            <span>Check-out</span>
            <div className="ljn-booking-field__control">
              <CalendarDays size={16} strokeWidth={1.6} />
              <input ref={checkoutRef} type="text" name="checkout" placeholder="Select date" readOnly />
            </div>
          </label>

          <label className="ljn-booking-field">
            <span>Adults</span>
            <div className="ljn-booking-field__control">
              <Users size={16} strokeWidth={1.6} />
              <input type="number" name="adults" min="1" defaultValue="2" />
            </div>
          </label>

          <label className="ljn-booking-field">
            <span>Children</span>
            <div className="ljn-booking-field__control">
              <Users size={16} strokeWidth={1.6} />
              <input type="number" name="children" min="0" defaultValue="0" />
            </div>
          </label>

          <label className="ljn-booking-field">
            <span>Promo code</span>
            <div className="ljn-booking-field__control">
              <Ticket size={16} strokeWidth={1.6} />
              <input type="text" name="promo" placeholder="Optional" />
            </div>
          </label>

          <button className="ljn-booking-modal__submit" type="submit">
            Reservation <ArrowRight size={16} strokeWidth={1.7} />
          </button>
        </div>
      </form>
    </div>
  )
}
